class TrainingCalendar extends HTMLElement {
  connectedCallback() {
    this.search = this.querySelector('[data-calendar-search]');
    this.category = this.querySelector('[data-calendar-category]');
    this.location = this.querySelector('[data-calendar-location]');
    this.month = this.querySelector('[data-calendar-month]');
    this.reset = this.querySelector('[data-calendar-reset]');
    this.empty = this.querySelector('[data-calendar-empty]');
    this.more = this.querySelector('[data-calendar-more]');
    this.grid = this.querySelector('.training-calendar__grid');
    this.cards = Array.from(this.querySelectorAll('[data-calendar-card]'));
    this.browse = this.hasAttribute('data-browse-collections');
    this.currentCollection = this.dataset.currentCollection || '';
    this.pageHandle = this.currentCollection || 'all';
    this.nextPage = 2;
    this.hasNext = Boolean(this.querySelector('[data-calendar-next]'));
    this.initialHasNext = this.hasNext;
    this.loading = false;
    this.requestId = 0;
    if (!this.search || !this.category || !this.location || !this.month) return;

    if (this.browse && this.category.value === this.currentCollection && this.currentCollection) {
      this.initialCards = this.cards.map((card) => card.cloneNode(true));
    }

    this.search.addEventListener('input', () => this.apply());
    this.search.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') event.preventDefault();
    });
    this.category.addEventListener('change', () => {
      if (this.browse) {
        this.loadCollection(this.category.value);
        return;
      }
      this.apply();
    });
    this.location.addEventListener('change', () => this.apply());
    this.month.addEventListener('change', () => this.apply());
    this.reset?.addEventListener('click', () => {
      this.search.value = '';
      this.location.value = '';
      this.month.value = '';
      if (this.browse) {
        this.category.value = this.currentCollection;
        if (this.category.value !== this.currentCollection) this.category.value = '';
        if (this.initialCards && this.category.value === this.currentCollection) {
          this.requestId += 1;
          this.loading = false;
          this.cards = this.initialCards.map((card) => card.cloneNode(true));
          this.grid?.replaceChildren(...this.cards);
          this.pageHandle = this.currentCollection || 'all';
          this.nextPage = 2;
          this.hasNext = this.initialHasNext;
          this.rebuildLocations(this.cards);
          this.rebuildMonths(this.cards);
          this.syncMore();
          this.apply();
          this.maybeContinue();
          return;
        }
        this.loadCollection(this.category.value);
        return;
      }
      this.category.value = '';
      this.apply();
    });

    this.observeMore();

    if (this.browse && this.category.value !== this.currentCollection) {
      this.loadCollection(this.category.value);
      return;
    }

    this.syncMore();
    this.maybeContinue();
  }

  observeMore() {
    if (!this.more || !this.browse) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
      },
      { rootMargin: '200px' }
    );
    this.observer.observe(this.more);
  }

  syncMore() {
    if (!this.more) return;
    const show = this.browse && (this.hasNext || this.loading);
    this.more.hidden = !show;
    this.more.textContent = this.loading ? this.more.dataset.loadingText || '' : '';
  }

  async fetchPage(handle, page) {
    const target = handle || 'all';
    const root = this.dataset.collectionsUrl || '/collections';
    const url = `${root}/${encodeURIComponent(target)}?view=calendar&page=${page}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('calendar');
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return {
      cards: Array.from(doc.querySelectorAll('[data-calendar-card]')),
      hasNext: Boolean(doc.querySelector('[data-calendar-next]')),
    };
  }

  cardKey(card) {
    const href = card.querySelector('a')?.getAttribute('href') || '';
    const variant = href.split('variant=')[1] || '';
    return variant || href || `${card.dataset.date || ''}|${card.dataset.title || ''}`;
  }

  mergeCards(existing, incoming) {
    const seen = new Set(existing.map((card) => this.cardKey(card)));
    const added = [];
    incoming.forEach((card) => {
      const key = this.cardKey(card);
      if (seen.has(key)) return;
      seen.add(key);
      added.push(card);
    });
    return { cards: [...existing, ...added], added: added.length };
  }

  renderCards(cards) {
    cards.sort((a, b) => (a.dataset.date || '').localeCompare(b.dataset.date || ''));
    this.grid?.replaceChildren(...cards);
    this.cards = cards;
    this.rebuildLocations(cards);
    this.rebuildMonths(cards);
    this.apply();
  }

  async loadCollection(handle) {
    const requestId = ++this.requestId;
    this.pageHandle = handle || 'all';
    this.nextPage = 1;
    this.hasNext = false;
    this.loading = true;
    this.cards = [];
    this.grid?.replaceChildren();
    this.rebuildLocations([]);
    this.rebuildMonths([]);
    this.syncMore();
    this.apply();

    try {
      const page = await this.fetchPage(this.pageHandle, 1);
      if (requestId !== this.requestId) return;
      this.renderCards(page.cards);
      this.hasNext = page.hasNext;
      this.nextPage = 2;
    } catch {
      if (requestId !== this.requestId) return;
      this.renderCards([]);
      this.hasNext = false;
    } finally {
      if (requestId !== this.requestId) return;
      this.loading = false;
      this.syncMore();
      if (!this.maybeContinue()) this.apply();
    }
  }

  async loadNext() {
    if (!this.browse || this.loading || !this.hasNext || this.nextPage > 20) return;
    const requestId = this.requestId;
    const pageNumber = this.nextPage;
    const handle = this.pageHandle;
    this.loading = true;
    this.syncMore();

    try {
      const page = await this.fetchPage(handle, pageNumber);
      if (requestId !== this.requestId) return;
      const merged = this.mergeCards(this.cards, page.cards);
      this.renderCards(merged.cards);
      this.hasNext = page.hasNext && pageNumber < 20 && merged.added > 0;
      this.nextPage = pageNumber + 1;
    } catch {
      if (requestId !== this.requestId) return;
      this.hasNext = false;
    } finally {
      if (requestId !== this.requestId) return;
      this.loading = false;
      this.syncMore();
      if (!this.maybeContinue()) this.apply();
    }
  }

  maybeContinue() {
    if (!this.browse || this.loading || !this.hasNext || !this.more || this.more.hidden) return false;
    const rect = this.more.getBoundingClientRect();
    if (rect.top > window.innerHeight + 200) return false;
    this.loadNext();
    return true;
  }

  rebuildSelect(select, cards, keyAttr, labelAttr) {
    if (!select) return;
    const selected = select.value;
    const allLabel = select.querySelector('option[value=""]')?.textContent || '';
    const labels = new Map();
    cards.forEach((card) => {
      const key = card.dataset[keyAttr];
      if (key) labels.set(key, card.dataset[labelAttr] || key);
    });
    const keys = Array.from(labels.keys()).sort((a, b) => a.localeCompare(b));
    select.replaceChildren();
    const all = document.createElement('option');
    all.value = '';
    all.textContent = allLabel;
    select.append(all);
    keys.forEach((key) => {
      const option = document.createElement('option');
      option.value = key;
      option.textContent = labels.get(key);
      select.append(option);
    });
    select.value = keys.includes(selected) ? selected : '';
  }

  rebuildLocations(cards) {
    this.rebuildSelect(this.location, cards, 'location', 'location');
  }

  rebuildMonths(cards) {
    const selected = this.month.value;
    const allLabel = this.month.querySelector('option[value=""]')?.textContent || '';
    const labels = new Map();
    cards.forEach((card) => {
      const key = card.dataset.month;
      if (key) labels.set(key, card.dataset.monthLabel || key);
    });
    const keys = Array.from(labels.keys()).sort();
    this.month.replaceChildren();
    const all = document.createElement('option');
    all.value = '';
    all.textContent = allLabel;
    this.month.append(all);
    keys.forEach((key) => {
      const option = document.createElement('option');
      option.value = key;
      option.textContent = labels.get(key);
      this.month.append(option);
    });
    this.month.value = keys.includes(selected) ? selected : '';
  }

  apply() {
    const query = this.search.value.trim().toLowerCase();
    const category = this.category.value;
    const location = this.location.value;
    const month = this.month.value;
    let visible = 0;

    this.cards.forEach((card) => {
      const title = card.dataset.title || '';
      const cardCategories = (card.dataset.category || '').split(/\s+/).filter(Boolean);
      const cardLocation = card.dataset.location || '';
      const cardMonth = card.dataset.month || '';
      const matchesTitle = query === '' || title.includes(query);
      const matchesCategory = this.browse || category === '' || cardCategories.includes(category);
      const matchesLocation = location === '' || cardLocation === location;
      const matchesMonth = month === '' || cardMonth === month;
      const show = matchesTitle && matchesCategory && matchesLocation && matchesMonth;
      card.hidden = !show;
      if (show) visible += 1;
    });

    if (!this.empty) return;
    if (this.empty.dataset.emptyText) this.empty.textContent = this.empty.dataset.emptyText;
    if (this.loading && visible === 0) {
      this.empty.hidden = true;
      return;
    }
    this.empty.hidden = visible !== 0;
    if (!this.loading) this.maybeContinue();
  }
}

if (!customElements.get('training-calendar')) {
  customElements.define('training-calendar', TrainingCalendar);
}
